import { Component, OnDestroy, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { BackgroundJobService } from '../../services/background-job.service';
import { interval, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { BACKGROUND_JOB_IDS, BackgroundJobType } from '../../shared/constants/background-jobs.constants';
import { SanitizationService } from '../../services/sanitization.service';
import { BackgroundJobStatus } from '../../models/background-job-status.model';

@Component({
    selector: 'app-hygiene-treatment',
    templateUrl: './hygiene-treatment.component.html',
    styleUrl: './hygiene-treatment.component.scss',
    standalone: false
})
export class HygieneTreatmentComponent implements OnInit, OnDestroy {
  loadingStates: Record<BackgroundJobType, boolean> = {
    [BackgroundJobType.CATEGORY]: true,
    [BackgroundJobType.DUPLICATES]: true,
    [BackgroundJobType.SEARCH]: true,
    [BackgroundJobType.PRIORITY]: true
  };

  private statusSubscription?: Subscription;

  readonly BackgroundJobType = BackgroundJobType;

  constructor(
    private messageService: MessageService,
    private backgroundJobService: BackgroundJobService,
    private sanitizationService: SanitizationService
  ) {}

  ngOnInit() {
    this.checkAllJobsStatus();
    this.statusSubscription = interval(5000)
      .pipe(switchMap(() => this.checkAllJobsStatus()))
      .subscribe();
  }

  ngOnDestroy() {
    this.statusSubscription?.unsubscribe();
  }

  private checkAllJobsStatus() {
    this.checkJobStatus(BACKGROUND_JOB_IDS.CATEGORY_CHECK, BackgroundJobType.CATEGORY);
    this.checkJobStatus(BACKGROUND_JOB_IDS.DEDUPLICATION, BackgroundJobType.DUPLICATES);
    this.checkJobStatus(BACKGROUND_JOB_IDS.CANDIDATES_SEARCH, BackgroundJobType.SEARCH);
    this.checkJobStatus(BACKGROUND_JOB_IDS.PRIORITY_CHECK, BackgroundJobType.PRIORITY);
    return [];
  }

  private checkJobStatus(jobId: string, type: BackgroundJobType) {
    this.backgroundJobService.getBackgroundJob(jobId).subscribe({
      next: (response: BackgroundJobStatus) => {
        this.loadingStates[type] = response.isRunning;
      }
    });
  }

  launchCategoryCheck() {
    this.sanitizationService.launchWorkOrderCategoryCheck().subscribe({
      next: () => {
        this.messageService.add({
          key: 'globalToast',
          severity: 'success',
          summary: 'Traitement lancé',
          detail: 'Vérification des catégories en cours...',
          life: 3000,
        });
        this.checkJobStatus(BACKGROUND_JOB_IDS.CATEGORY_CHECK, BackgroundJobType.CATEGORY);
      },
      error: () => {
        this.messageService.add({
          key: 'globalToast',
          severity: 'error',
          summary: 'Erreur',
          detail: 'Impossible de lancer la vérification des catégories',
          life: 3000,
        });
      }
    });
  }

  launchDuplicatesCheck() {
    this.sanitizationService.launchWorkOrderDeduplication().subscribe({
      next: () => {
        this.messageService.add({
          key: 'globalToast',
          severity: 'success',
          summary: 'Traitement lancé',
          detail: 'Vérification des doublons en cours...',
          life: 3000,
        });
        this.checkJobStatus(BACKGROUND_JOB_IDS.DEDUPLICATION, BackgroundJobType.DUPLICATES);
      },
      error: () => {
        this.messageService.add({
          key: 'globalToast',
          severity: 'error',
          summary: 'Erreur',
          detail: 'Impossible de lancer la vérification des doublons',
          life: 3000,
        });
      }
    });
  }

  launchSearchCandidates() {
    this.sanitizationService.launchNotificationCandidatesSearch().subscribe({
      next: () => {
        this.messageService.add({
          key: 'globalToast',
          severity: 'success',
          summary: 'Traitement lancé',
          detail: 'Recherche de candidats en cours...',
          life: 3000,
        });
        this.checkJobStatus(BACKGROUND_JOB_IDS.CANDIDATES_SEARCH, BackgroundJobType.SEARCH);
      },
      error: () => {
        this.messageService.add({
          key: 'globalToast',
          severity: 'error',
          summary: 'Erreur',
          detail: 'Impossible de lancer la recherche de candidats',
          life: 3000,
        });
      }
    });
  }

  launchPriorityCheck() {
    this.sanitizationService.launchPriorizationCheck().subscribe({
      next: () => {
        this.messageService.add({
          key: 'globalToast',
          severity: 'success',
          summary: 'Traitement lancé',
          detail: 'Vérification des priorités en cours...',
          life: 3000,
        });
        this.checkJobStatus(BACKGROUND_JOB_IDS.PRIORITY_CHECK, BackgroundJobType.PRIORITY);
      },
      error: () => {
        this.messageService.add({
          key: 'globalToast',
          severity: 'error',
          summary: 'Erreur',
          detail: 'Impossible de lancer la vérification des priorités',
          life: 3000,
        });
      }
    });
  }
}
